import React, { useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, Building, RotateCcw } from "lucide-react";
import formatApiError from "../../lib/apiError";
import useActiveInactive from "../../lib/useActiveInactive";
import ActiveInactiveTabs from "../../components/ActiveInactiveTabs";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });
const BASE = `${API}/api/universities`;

const EMPTY_FORM = { name: "", description: "" };

export default function UniversitiesPage() {
  const { view, setView, list: universities, inactiveCount, loading, error, reload } = useActiveInactive(BASE);

  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [formErr, setFormErr] = useState("");
  const [saving, setSaving]   = useState(false);

  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const openAdd  = () => { setForm(EMPTY_FORM); setFormErr(""); setModal({ mode: "add", university: null }); };
  const openEdit = (u) => {
    setForm({ name: u.name, description: u.description || "" });
    setFormErr(""); setModal({ mode: "edit", university: u });
  };
  const closeModal = () => { setModal(null); setFormErr(""); };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormErr("Name is required."); return; }
    setSaving(true); setFormErr("");
    try {
      if (modal.mode === "add") await axios.post(BASE, form, ax());
      else                       await axios.put(`${BASE}/${modal.university.id}`, form, ax());
      closeModal(); reload();
    } catch (e) { setFormErr(formatApiError(e, "Something went wrong.")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await axios.delete(`${BASE}/${confirmId}`, ax()); setConfirmId(null); reload(); }
    catch (e) { alert(formatApiError(e, "Could not delete.")); setConfirmId(null); }
    finally { setDeleting(false); }
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try { await axios.post(`${BASE}/${id}/restore`, {}, ax()); reload(); }
    catch (e) { alert(formatApiError(e, "Could not restore.")); }
    finally { setRestoringId(null); }
  };

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Universities</h1>
          <p className="page-sub">Reference list of universities used when adding colleges.</p>
        </div>
        {view === "active" && (
          <button className="btn-primary" onClick={openAdd} data-testid="add-university-btn">
            <Plus size={16} /> Add University
          </button>
        )}
      </div>

      <ActiveInactiveTabs view={view} onChange={setView} inactiveCount={inactiveCount} testIdPrefix="universities-view" />

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {universities.length === 0 ? (
            <div className="empty-state">
              <Building size={32} color="#43E0FF" />
              {view === "active"
                ? <p>No universities yet. Click <strong>Add University</strong> to create one.</p>
                : <p>No inactive universities.</p>}
            </div>
          ) : (
            <table className="data-table" data-testid="universities-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {universities.map((u, i) => (
                  <tr key={u.id} data-testid={`university-row-${u.id}`}>
                    <td className="td-num">{i + 1}</td>
                    <td className="td-name">{u.name}</td>
                    <td className="td-desc">{u.description || <span className="td-empty">—</span>}</td>
                    <td>
                      <div className="action-btns">
                        {view === "active" ? (
                          <>
                            <button className="action-edit"   onClick={() => openEdit(u)}        title="Edit"   data-testid={`edit-university-${u.id}`}><Pencil size={15} /></button>
                            <button className="action-delete" onClick={() => setConfirmId(u.id)} title="Delete" data-testid={`delete-university-${u.id}`}><Trash2 size={15} /></button>
                          </>
                        ) : (
                          <button className="action-restore" onClick={() => handleRestore(u.id)} disabled={restoringId === u.id} data-testid={`restore-university-${u.id}`} title="Restore"><RotateCcw size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()} data-testid="university-modal">
            <div className="modal-head">
              <h2 className="modal-title">{modal.mode === "add" ? "Add University" : "Edit University"}</h2>
              <button className="modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            {formErr && <div className="form-error" data-testid="form-error">{formErr}</div>}
            <div className="modal-body">
              <div className="field">
                <label className="field-label">Name <span className="required">*</span></label>
                <input className="field-input" placeholder="e.g. Rajiv Gandhi University of Health Sciences" value={form.name}
                  onChange={onChange("name")} maxLength={200} autoFocus data-testid="university-name-input" />
              </div>
              <div className="field">
                <label className="field-label">Description</label>
                <textarea className="field-input field-textarea" placeholder="Optional notes" value={form.description}
                  onChange={onChange("description")} rows={3} maxLength={600} data-testid="university-description-input" />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-university-btn">
                {saving ? <span className="btn-spinner" /> : modal.mode === "add" ? "Add University" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="modal-head">
              <h2 className="modal-title">Delete University</h2>
              <button className="modal-close" onClick={() => setConfirmId(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">Move this university to <strong>Inactive</strong>? You cannot deactivate one that still has active colleges linked.</p>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <span className="btn-spinner" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
